/**
 * iframe Detection Utilities
 * Story 7.2 - Task 2: Responsive iframe detection and scaling
 *
 * Detects when app is running inside an iframe and provides utilities
 * for iframe-specific behavior (focus indicators, fullscreen, scaling)
 */

// Extend interfaces for vendor-prefixed fullscreen APIs
interface ExtendedHTMLElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

interface ExtendedDocument extends Document {
  webkitFullscreenElement?: Element;
  msFullscreenElement?: Element;
  webkitExitFullscreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

/**
 * Detects if the application is running inside an iframe
 * @returns true if running in iframe, false if standalone
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // If we can't access window.top due to cross-origin restrictions,
    // we're definitely in an iframe
    return true;
  }
}

/**
 * Gets the dimensions of the iframe (if running in one)
 * @returns Iframe dimensions or null if standalone
 */
export function getIframeDimensions(): { width: number; height: number } | null {
  if (!isInIframe()) {
    return null;
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
  };
}

/**
 * Checks if iframe is constrained (smaller than optimal size)
 * @param minWidth Minimum optimal width (default: 400px)
 * @param minHeight Minimum optimal height (default: 700px)
 * @returns true if iframe is smaller than optimal dimensions
 */
export function isIframeConstrained(minWidth = 400, minHeight = 700): boolean {
  const dimensions = getIframeDimensions();
  if (!dimensions) {
    return false;
  }

  return dimensions.width < minWidth || dimensions.height < minHeight;
}

/**
 * Adds focus/blur event listeners to show visual indicator when iframe is focused
 * Adds/removes 'iframe-focused' class to document.body
 */
export function setupIframeFocusIndicator(): () => void {
  if (!isInIframe()) {
    return () => {}; // No-op cleanup
  }

  const handleFocus = () => {
    document.body.classList.add('iframe-focused');
  };

  const handleBlur = () => {
    document.body.classList.remove('iframe-focused');
  };

  window.addEventListener('focus', handleFocus);
  window.addEventListener('blur', handleBlur);

  // Return cleanup function
  return () => {
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('blur', handleBlur);
    document.body.classList.remove('iframe-focused');
  };
}

/**
 * Requests fullscreen mode (requires user gesture)
 * @returns Promise that resolves when fullscreen is entered, rejects if not supported or denied
 */
export async function requestFullscreen(): Promise<void> {
  const elem = document.documentElement;

  const extendedElem = elem as ExtendedHTMLElement;
  try {
    if (elem.requestFullscreen) {
      await elem.requestFullscreen();
    } else if (extendedElem.webkitRequestFullscreen) {
      // Safari support
      await extendedElem.webkitRequestFullscreen();
    } else if (extendedElem.msRequestFullscreen) {
      // IE11 support
      await extendedElem.msRequestFullscreen();
    } else {
      throw new Error('Fullscreen API not supported');
    }
  } catch (error) {
    console.error('Fullscreen request failed:', error);
    throw error;
  }
}

/**
 * Checks if fullscreen is currently active
 * @returns true if in fullscreen mode
 */
export function isFullscreen(): boolean {
  const extendedDoc = document as ExtendedDocument;
  return !!(
    document.fullscreenElement ||
    extendedDoc.webkitFullscreenElement ||
    extendedDoc.msFullscreenElement
  );
}

/**
 * Exits fullscreen mode
 * @returns Promise that resolves when fullscreen is exited
 */
export async function exitFullscreen(): Promise<void> {
  const extendedDoc = document as ExtendedDocument;
  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if (extendedDoc.webkitExitFullscreen) {
      await extendedDoc.webkitExitFullscreen();
    } else if (extendedDoc.msExitFullscreen) {
      await extendedDoc.msExitFullscreen();
    }
  } catch (error) {
    console.error('Exit fullscreen failed:', error);
    throw error;
  }
}
